/**
 * Scope Context
 *
 * Provides global state management for Personal vs Managed scope switching.
 * This enables mentor masquerade mode and client view switching across the app.
 */

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useAuth } from "../auth/AuthContext";

const SCOPE_STORAGE_KEY = "creditGuide_activeScope";

/**
 * Get scope state from sessionStorage
 */
function getScopeFromStorage() {
  try {
    const stored = sessionStorage.getItem(SCOPE_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error reading scope from storage:", error);
  }
  return null;
}

/**
 * Save scope state to sessionStorage
 */
function saveScopeToStorage(scopeState) {
  try {
    if (scopeState) {
      sessionStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(scopeState));
    } else {
      sessionStorage.removeItem(SCOPE_STORAGE_KEY);
    }
  } catch (error) {
    console.error("Error saving scope to storage:", error);
  }
}

// Create the context
export const ScopeContext = createContext(null);

/**
 * Scope Provider Component
 * Wrap your app with this to enable scope switching
 */
export const ScopeProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [scopeState, setScopeState] = useState(() => getScopeFromStorage());

  // Clear scope when user logs out
  useEffect(() => {
    if (!currentUser) {
      setScopeState(null);
      saveScopeToStorage(null);
    }
  }, [currentUser]);

  // Sync scope state to sessionStorage
  useEffect(() => {
    saveScopeToStorage(scopeState);
  }, [scopeState]);

  /**
   * Enter managed scope (mentor masquerading as client)
   * @param {string} clientUserId - The client's user ID
   * @param {string} clientName - The client's display name (optional)
   */
  const enterManagedScope = useCallback(
    (clientUserId, clientName = null) => {
      if (!currentUser) {
        console.error("Cannot enter managed scope: no current user");
        return;
      }

      console.log(
        "[ScopeContext] Entering managed scope:",
        clientUserId,
        clientName
      );
      setScopeState({
        isManaged: true,
        mentorId: currentUser.uid,
        effectiveUserId: clientUserId,
        clientName,
      });
    },
    [currentUser]
  );

  /**
   * Enter client view scope (client viewing mentor-created content)
   * @param {string} mentorUserId - The mentor's user ID
   * @param {string} mentorName - The mentor's display name (optional)
   */
  const enterClientViewScope = useCallback(
    (mentorUserId, mentorName = null) => {
      if (!currentUser) {
        console.error("Cannot enter client view scope: no current user");
        return;
      }

      console.log(
        "[ScopeContext] Entering client view scope:",
        mentorUserId,
        mentorName
      );
      setScopeState({
        isManaged: true,
        isClientView: true, // Flag to distinguish client view from mentor masquerade
        mentorId: mentorUserId,
        effectiveUserId: currentUser.uid, // Client views their own data
        clientName: mentorName, // Reuse this field for mentor name in client view
      });
    },
    [currentUser]
  );

  /**
   * Exit managed scope and return to personal scope
   */
  const exitManagedScope = useCallback(() => {
    console.log("[ScopeContext] Exiting managed scope");
    setScopeState(null);
  }, []);

  // Compute derived values
  const isManaged = scopeState?.isManaged || false;
  const effectiveUserId =
    scopeState?.effectiveUserId || currentUser?.uid || null;
  const mentorId = scopeState?.mentorId || null;
  const clientName = scopeState?.clientName || null;
  const actualUserId = currentUser?.uid || null;
  const isClientView = scopeState?.isClientView || false;

  /**
   * Get the current role based on scope
   */
  const getRole = useCallback(() => {
    if (isManaged && !isClientView) {
      return "mentor";
    }
    if (isManaged && isClientView) {
      return "client";
    }
    return "regularUser";
  }, [isManaged, isClientView]);

  const value = {
    // State
    isManaged,
    effectiveUserId,
    mentorId,
    clientName,
    actualUserId,
    isClientView,

    // Actions
    enterManagedScope,
    enterClientViewScope,
    exitManagedScope,
    getRole,
  };

  return (
    <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
  );
};

export default ScopeContext;
